// src/pages/centers/CenterManagementPage.js
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";
import useDataStore from "../../store/dataStore";

// import "./CenterManagementPage.css";

const BASE_URL = "https://mis.empowerplus.in";

export default function CenterManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "centers";

    // ---------- common ----------
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
        }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : null;
        } catch {
            return text;
        }
    };

    // ---------- upload ----------
    const [file, setFile] = useState(null);
    const [actor, setActor] = useState("Admin");
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    // ---------- table ----------
    const [centers, setCenters] = useState(() => store.getRows(CACHE_KEY));
    const [loadingCenters, setLoadingCenters] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [visibleCount, setVisibleCount] = useState(100);
    const [search, setSearch] = useState("");
    const [totalCount, setTotalCount] = useState(null);

    // ---------- edit (PATCH single) ----------
    const [editingCenter, setEditingCenter] = useState(null); // full row (from table)
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    // ---------- audits ----------
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditCenterId, setAuditCenterId] = useState("");

    // Initial load — first 100 rows only (fast)
    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setCenters(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoadingCenters(true);
        setAllLoaded(false);
        try {
            const res = await fetch(`${BASE_URL}/api/centers?page=0&size=100`, { headers: authHeaders });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load centers");
            const partial = Array.isArray(data?.content) ? data.content : [];
            setCenters(partial);
            store.setRows(CACHE_KEY, partial, false);
        } catch (e) {
            flash("err", e.message);
            setCenters([]);
        } finally {
            setLoadingCenters(false);
        }
    };

    // Load all centers at once (triggered by "Load All" button or after mutations)
    const fetchAllCenters = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/centers/all`, { headers: authHeaders });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load centers");
            const all = Array.isArray(data) ? data : [];
            setCenters(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch (e) {
            flash("err", e.message);
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.centers ?? null); })
            .catch(() => {});
    }, []);

    // ------------------- Upload Excel (client parse → JSON chunks) -------------------
    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Please choose an Excel file first");

        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }

        if (!dataRows.length) return flash("err", "Excel file is empty");

        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

        let totalInserted = 0, totalSkipped = 0, failed = false, firstError = "";

        const uploadOneChunk = async (i) => {
            const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const res = await fetch(
                `${BASE_URL}/api/centers/upload-json?actor=${encodeURIComponent(actor || "Admin")}`,
                {
                    method: "POST",
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    body: JSON.stringify(chunkRows),
                }
            );
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const data = await res.json();
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

            const inserted = data?.inserted ?? 0;
            const skipped = data?.skipped ?? 0;
            const skippedRows = data?.skippedRows ?? [];

            console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${inserted} | Skipped: ${skipped}`);
            if (skippedRows.length > 0) {
                console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
                skippedRows.forEach(r => console.log(r));
                console.groupEnd();
            }
            return { inserted, skipped };
        };

        let uploadedSoFar = 0;
        for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
            const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

            const results = await Promise.allSettled(indices.map(uploadOneChunk));
            for (const r of results) {
                if (r.status === "fulfilled") {
                    totalInserted += r.value.inserted;
                    totalSkipped += r.value.skipped;
                    uploadedSoFar += r.value.inserted + r.value.skipped;
                } else {
                    failed = true;
                    firstError = r.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            console.log(`[Upload Summary] Inserted: ${totalInserted} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllCenters();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // ------------------- Download Table Excel (local export) -------------------
    const [exporting, setExporting] = useState(false);

    const downloadTableExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const res = await fetch(`${BASE_URL}/api/centers/export`, { headers: authHeaders });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "centers.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            flash("err", err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    // ------------------- Edit / Patch Single -------------------
    const openEdit = (row) => {
        if (!row?.centerId) {
            flash("err", "centerId missing");
            return;
        }

        setEditingCenter(row);

        setEditForm({
            projectName: row.projectName ?? "",

            trainingCentreName: row.trainingCentreName ?? "",
            centerType: row.centerType ?? "",
            sidhCenterId: row.sidhCenterId ?? "",

            address: row.address ?? "",
            nearbyLandmark: row.nearbyLandmark ?? "",
            district: row.district ?? "",
            state: row.state ?? "",
            pincode: row.pincode ?? "",
            geoLocation: row.geoLocation ?? "",

            areaSqft: row.areaSqft ?? "",
            totalClassroom: row.totalClassroom ?? "",
            totalLabs: row.totalLabs ?? "",
            itLab: row.itLab ?? "",

            maleWashroom: row.maleWashroom ?? "",
            femaleWashroom: row.femaleWashroom ?? "",
            unisexWashroom: row.unisexWashroom ?? "",
            receptionArea: row.receptionArea ?? "",

            spocName: row.spocName ?? "",
            spocMobile: row.spocMobile ?? "",
            spocEmail: row.spocEmail ?? "",

            addressProofDocument: row.addressProofDocument ?? "",
        });
    };


    const patchCenter = async () => {
        if (!editingCenter?.centerId) return;

        setSaving(true);
        try {
            const qs = new URLSearchParams({
                centerId: editingCenter.centerId,
                actor: actor || "SYSTEM",
            }).toString();

            const payload = { ...editForm };

            // convert numeric fields safely
            ["totalClassroom", "maleWashroom"].forEach((k) => {
                if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
                    delete payload[k];
                    return;
                }
                const n = Number(payload[k]);
                if (!Number.isNaN(n)) payload[k] = n;
            });

            // remove empty strings (treat as not provided)
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
                    delete payload[k];
                }
            });

            const res = await fetch(`${BASE_URL}/api/centers?${qs}`, {
                method: "PATCH",
                headers: jsonHeaders,
                body: JSON.stringify(payload),
            });

            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Patch failed");

            flash("ok", "Center updated");
            setEditingCenter(null);
            setEditForm({});
            store.invalidate(CACHE_KEY);
            fetchAllCenters();
        } catch (e) {
            flash("err", e.message);
        } finally {
            setSaving(false);
        }
    };

    const FormField = ({ label, value, onChange, type = "text", placeholder }) => (
        <div className="field">
            <label>{label}</label>
            <input
                type={type}
                value={value ?? ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );


    // ------------------- Soft Delete -------------------
    const deactivateCenter = async (centerId) => {
        if (!centerId) return;
        if (!window.confirm(`Deactivate center ${centerId}?`)) return;

        try {
            const qs = new URLSearchParams({
                actor: actor || "SYSTEM",
            }).toString();

            const res = await fetch(`${BASE_URL}/api/centers?centerId=${encodeURIComponent(centerId)}&${qs}`, {

                method: "DELETE",
                headers: authHeaders,
            });

            if (res.status === 204) {
                flash("ok", "Center deactivated");
                store.invalidate(CACHE_KEY);
                fetchAllCenters();
                return;
            }

            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Delete failed");
            flash("ok", "Center deactivated");
            store.invalidate(CACHE_KEY);
            fetchAllCenters();
        } catch (e) {
            flash("err", e.message);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/center_creation_template.xlsx"; // local path
        a.download = "center_creation_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // ------------------- Audits -------------------
    const openAuditsAll = async () => {
        setAuditOpen(true);
        setAuditCenterId("");
        setAuditLoading(true);
        setAuditRows([]);
        try {
            const res = await fetch(`${BASE_URL}/api/centers/audits`, { headers: authHeaders });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load audits");
            setAuditRows(Array.isArray(data) ? data : []);
        } catch (e) {
            flash("err", e.message);
        } finally {
            setAuditLoading(false);
        }
    };

    const openAuditsByCenter = async (centerId) => {
        console.log(centerId)
        setAuditOpen(true);
        setAuditCenterId(centerId);
        setAuditLoading(true);
        setAuditRows([]);
        try {
            const res = await fetch(
                `${BASE_URL}/api/centers/audits/by-center?centerId=${encodeURIComponent(centerId)}`,

                { headers: authHeaders }
            );
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load audits");
            setAuditRows(Array.isArray(data) ? data : []);
        } catch (e) {
            flash("err", e.message);
        } finally {
            setAuditLoading(false);
        }
    };

    const closeAudit = () => {
        setAuditOpen(false);
        setAuditRows([]);
        setAuditCenterId("");
    };

    // ------------------- Search & Columns -------------------
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return centers;
        return centers.filter((c) => {
            const id = String(c.centerId || "").toLowerCase();
            const name = String(c.trainingCentreName || c.centerName || "").toLowerCase();
            const proj = String(c.projectName || "").toLowerCase();
            return id.includes(q) || name.includes(q) || proj.includes(q);
        });
    }, [centers, search]);

    const columns = useMemo(() => {
        const sample = filtered[0] || centers[0];
        if (!sample) return [];
        const keys = Object.keys(sample);
        const priority = ["centerId"];
        const rest = keys.filter((k) => !priority.includes(k));
        return [...priority.filter((k) => keys.includes(k)), ...rest];
    }, [filtered, centers]);

    return (
        <>
            <div className="cmp-page">
                <div className="cmp-header">
                    <div>
                        <h1>Center Management</h1>
                        {/* <p>
                            Excel UPSERT/UPDATE_ONLY, Single PATCH update, Soft delete, Audit logs
                        </p> */}
                    </div>
                    <div className="cmp-header-actions mt-1">
                        <button className="btn" onClick={fetchFirst100} disabled={loadingCenters}>
                            {loadingCenters ? "Refreshing..." : "Refresh"}
                        </button>
                        {/* <button className="btn btn-outline" onClick={openAuditsAll}>
                            View All Audits
                        </button> */}
                    </div>
                </div>

                {msg.text ? (
                    <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>{msg.text}</span>
                        <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                    </div>
                ) : null}

                {/* Upload progress bar */}
                {uploadProgress && (
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                                {uploadProgress.error
                                    ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                    : uploading
                                        ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                        : "Upload complete"}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                                {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                                &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                            </span>
                        </div>
                        <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                            <div style={{
                                height: "100%",
                                width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                                background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                                borderRadius: 999,
                                transition: "width 0.3s ease",
                            }} />
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                            {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                        </div>
                    </div>
                )}

                {/* Upload Card */}
                <div className="card">
                    <div className="card-title">1️⃣ Excel Upload</div>

                    <div className="grid-4">
                        <div className="field">
                            <label>Excel File *</label>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <div className="hint">{file ? `Selected: ${file.name}` : "Choose .xlsx/.xls"}</div>
                        </div>
                        {/* 
                        <div className="field">
                            <label>Mode</label>
                            <select value={mode} onChange={(e) => setMode(e.target.value)}>
                                <option value="UPSERT">UPSERT (Create + Update)</option>
                                <option value="UPDATE_ONLY">UPDATE_ONLY (Only update existing)</option>
                            </select>
                            <div className="hint">Sent as query param: mode</div>
                        </div> */}

                        {/* <div className="field" >
                            <label>createdBy</label>
                            <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Admin" />
                            <div className="hint">.</div>
                        </div> */}

                        <div className="field end" style={{ marginBottom: "30px" }}>
                            <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                                {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                            </button>
                        </div>
                    </div>

                    <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", marginTop: "1rem" }}>
                        <button className="btn btn-outline" onClick={downloadTemplate}>
                            Download Excel Template
                        </button>

                    </div>

                    {/* {uploadResult ? (
                        <div className="result">
                            <div className="result-title">Upload Result</div>

                            <div className="result-grid">
                                <div className="kv"><span>Total Rows</span><b>{uploadResult.totalRows ?? 0}</b></div>
                                <div className="kv"><span>Created</span><b>{uploadResult.created ?? 0}</b></div>
                                <div className="kv"><span>Updated</span><b>{uploadResult.updated ?? 0}</b></div>
                                <div className="kv"><span>Skipped</span><b>{uploadResult.skipped ?? 0}</b></div>
                                <div className="kv"><span>Failed</span><b>{uploadResult.failed ?? 0}</b></div>
                            </div>

                            {Array.isArray(uploadResult.errors) && uploadResult.errors.length ? (
                                <div className="errors">
                                    <div className="result-title">Row Errors</div>
                                    {uploadResult.errors.map((er, i) => (
                                        <div className="err-item" key={i}>
                                            Row {er.rowNumber}: {er.message}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="ok">No row errors ✅</div>
                            )}
                        </div>
                    ) : null} */}
                </div>

                {/* Table Card */}
                <div className="card">
                    <div className="card-top">
                        <div className="card-title">2️⃣ Centers Table</div>
                        <div className="table-actions">
                            <input
                                className="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by centerId / name / project / department..."
                            />
                            <button className="btn btn-outline" onClick={downloadTableExcel} disabled={!centers.length || exporting}>
                                {exporting ? "Processing..." : "Export Excel"}
                            </button>
                        </div>
                    </div>
                    {totalCount !== null && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                            Total: {totalCount.toLocaleString()}
                        </div>
                    )}

                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {columns.length ? columns.map((c) => <th key={c}>{c}</th>) : <th>Centers</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={Math.max(columns.length + 2, 3)} className="muted">
                                            {loadingCenters ? "Loading..." : "No centers found"}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.slice(0, visibleCount).map((c, idx) => (
                                        <tr key={c.centerId || idx}>
                                            <td>{idx + 1}</td>
                                            {columns.map((k) => (
                                                <td key={k}>
                                                    {typeof c[k] === "object" ? JSON.stringify(c[k]) : String(c[k] ?? "")}
                                                </td>
                                            ))}
                                            <td className="actions">
                                                <button className="btn btn-sm btn-danger" onClick={() => deactivateCenter(c.centerId)}>
                                                    Deactivate
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                {!allLoaded && !loadingCenters && (
                                    <tr>
                                        <td colSpan={Math.max(columns.length + 2, 3)} style={{ textAlign: "center", padding: 14 }}>
                                            <button className="btn btn-outline" onClick={fetchAllCenters} disabled={loadingAll}>
                                                {loadingAll ? "Loading all data…" : "Load All Data"}
                                            </button>
                                        </td>
                                    </tr>
                                )}
                                {allLoaded && filtered.length > visibleCount && (
                                    <tr>
                                        <td colSpan={Math.max(columns.length + 2, 3)} style={{ textAlign: "center", padding: 14 }}>
                                            <button className="btn btn-outline" onClick={() => setVisibleCount(c => c + 100)}>
                                                Show More ({filtered.length - visibleCount} remaining)
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* <div className="note">
                        <b>Note:</b> Table requires backend <code>GET /api/centers</code>. If not available, tell me and I’ll adjust page to show results only.
                    </div> */}
                </div>

                {/* EDIT MODAL (PATCH /api/centers?centerId=...&actor=...) */}


                {editingCenter &&
                    createPortal(
                        <div className="modal-overlay" onClick={() => setEditingCenter(null)}>
                            <div className="ep-modal" onClick={(e) => e.stopPropagation()}>

                                {/* ---------- HEADER ---------- */}
                                <div className="modal-head">
                                    <div>
                                        <div className="modal-title">Edit Center</div>
                                        <div className="modal-sub">
                                            Center ID: <b>{editingCenter.centerId}</b>
                                        </div>
                                    </div>
                                    <button className="icon-btn" onClick={() => setEditingCenter(null)}>✕</button>
                                </div>

                                {/* ---------- FORM ---------- */}
                                <div className="modal-body">
                                    <div className="form-grid">

                                        {/* ---------- PROJECT ---------- */}
                                        <FormField
                                            label="Project Name"
                                            value={editForm.projectName}
                                            onChange={(v) => setEditForm(p => ({ ...p, projectName: v }))}
                                        />

                                        {/* ---------- CENTER BASIC ---------- */}
                                        <FormField
                                            label="Training Centre Name"
                                            value={editForm.trainingCentreName}
                                            onChange={(v) => setEditForm(p => ({ ...p, trainingCentreName: v }))}
                                        />

                                        <FormField
                                            label="Center Type"
                                            placeholder="Training / Assessment"
                                            value={editForm.centerType}
                                            onChange={(v) => setEditForm(p => ({ ...p, centerType: v }))}
                                        />

                                        <FormField
                                            label="SIDH Center ID"
                                            value={editForm.sidhCenterId}
                                            onChange={(v) => setEditForm(p => ({ ...p, sidhCenterId: v }))}
                                        />

                                        {/* ---------- LOCATION ---------- */}
                                        <FormField
                                            label="Address"
                                            value={editForm.address}
                                            onChange={(v) => setEditForm(p => ({ ...p, address: v }))}
                                        />

                                        <FormField
                                            label="Nearby Landmark"
                                            value={editForm.nearbyLandmark}
                                            onChange={(v) => setEditForm(p => ({ ...p, nearbyLandmark: v }))}
                                        />

                                        <FormField
                                            label="District"
                                            value={editForm.district}
                                            onChange={(v) => setEditForm(p => ({ ...p, district: v }))}
                                        />

                                        <FormField
                                            label="State"
                                            value={editForm.state}
                                            onChange={(v) => setEditForm(p => ({ ...p, state: v }))}
                                        />

                                        <FormField
                                            label="Pincode"
                                            value={editForm.pincode}
                                            onChange={(v) => setEditForm(p => ({ ...p, pincode: v }))}
                                        />

                                        <FormField
                                            label="Geo Location"
                                            placeholder="lat,long"
                                            value={editForm.geoLocation}
                                            onChange={(v) => setEditForm(p => ({ ...p, geoLocation: v }))}
                                        />

                                        {/* ---------- INFRASTRUCTURE ---------- */}
                                        <FormField
                                            label="Area (Sqft)"
                                            type="number"
                                            value={editForm.areaSqft}
                                            onChange={(v) => setEditForm(p => ({ ...p, areaSqft: v }))}
                                        />

                                        <FormField
                                            label="Total Classrooms"
                                            type="number"
                                            value={editForm.totalClassroom}
                                            onChange={(v) => setEditForm(p => ({ ...p, totalClassroom: v }))}
                                        />

                                        <FormField
                                            label="Total Labs"
                                            type="number"
                                            value={editForm.totalLabs}
                                            onChange={(v) => setEditForm(p => ({ ...p, totalLabs: v }))}
                                        />

                                        <FormField
                                            label="IT Labs"
                                            type="number"
                                            value={editForm.itLab}
                                            onChange={(v) => setEditForm(p => ({ ...p, itLab: v }))}
                                        />

                                        {/* ---------- WASHROOM ---------- */}
                                        <FormField
                                            label="Male Washroom"
                                            type="number"
                                            value={editForm.maleWashroom}
                                            onChange={(v) => setEditForm(p => ({ ...p, maleWashroom: v }))}
                                        />

                                        <FormField
                                            label="Female Washroom"
                                            type="number"
                                            value={editForm.femaleWashroom}
                                            onChange={(v) => setEditForm(p => ({ ...p, femaleWashroom: v }))}
                                        />

                                        <FormField
                                            label="Unisex Washroom"
                                            type="number"
                                            value={editForm.unisexWashroom}
                                            onChange={(v) => setEditForm(p => ({ ...p, unisexWashroom: v }))}
                                        />

                                        <FormField
                                            label="Reception Area"
                                            placeholder="YES / NO"
                                            value={editForm.receptionArea}
                                            onChange={(v) => setEditForm(p => ({ ...p, receptionArea: v }))}
                                        />

                                        {/* ---------- SPOC ---------- */}
                                        <FormField
                                            label="SPOC Name"
                                            value={editForm.spocName}
                                            onChange={(v) => setEditForm(p => ({ ...p, spocName: v }))}
                                        />

                                        <FormField
                                            label="SPOC Mobile"
                                            value={editForm.spocMobile}
                                            onChange={(v) => setEditForm(p => ({ ...p, spocMobile: v }))}
                                        />

                                        <FormField
                                            label="SPOC Email"
                                            type="email"
                                            value={editForm.spocEmail}
                                            onChange={(v) => setEditForm(p => ({ ...p, spocEmail: v }))}
                                        />

                                        {/* ---------- DOCUMENT ---------- */}
                                        <FormField
                                            label="Address Proof Document"
                                            value={editForm.addressProofDocument}
                                            onChange={(v) => setEditForm(p => ({ ...p, addressProofDocument: v }))}
                                        />

                                    </div>


                                    {/* ---------- PAYLOAD PREVIEW ---------- */}
                                    {/* <div className="preview">
                                        <div className="preview-title">PATCH Payload Preview</div>
                                        <pre>{JSON.stringify(editForm, null, 2)}</pre>
                                    </div> */}
                                </div>

                                {/* ---------- FOOTER ---------- */}
                                <div className="modal-actions">
                                    <button className="btn btn-outline" onClick={() => setEditingCenter(null)}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={patchCenter} disabled={saving}>
                                        {saving ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )
                }




                {auditOpen &&
                    createPortal(
                        <div className="modal-overlay" onClick={closeAudit}>
                            <div
                                className="ep-modal ep-modal-wide"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* ---------- HEADER ---------- */}
                                <div className="modal-head">
                                    <div>
                                        <div className="modal-title">Center Audit Logs</div>
                                        <div className="modal-sub">
                                            {auditCenterId ? (
                                                <>Center ID: <b>{auditCenterId}</b></>
                                            ) : (
                                                <>All Audits</>
                                            )}
                                        </div>
                                    </div>
                                    <button className="icon-btn" onClick={closeAudit}>✕</button>
                                </div>

                                {/* ---------- BODY ---------- */}
                                <div className="modal-body">
                                    {auditLoading ? (
                                        <div className="muted">Loading audits...</div>
                                    ) : auditRows.length === 0 ? (
                                        <div className="muted">No audit logs found</div>
                                    ) : (
                                        <div className="table-wrap">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Action</th>
                                                        <th>Method</th>
                                                        <th>Actor</th>
                                                        <th>Center</th>
                                                        <th>Created At</th>
                                                        <th>Before</th>
                                                        <th>After</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {auditRows.map((a) => (
                                                        <tr key={a.id || `${a.centerId}-${a.createdAt}`}>
                                                            <td>{a.id ?? "-"}</td>
                                                            <td>{a.action ?? "-"}</td>
                                                            <td>{a.method ?? "-"}</td>
                                                            <td>{a.actor ?? "-"}</td>
                                                            <td>{a.centerId ?? "-"}</td>
                                                            <td>{a.createdAt ?? "-"}</td>
                                                            <td>
                                                                <pre className="pre-small">
                                                                    {String(a.beforeJson ?? "")}
                                                                </pre>
                                                            </td>
                                                            <td>
                                                                <pre className="pre-small">
                                                                    {String(a.afterJson ?? "")}
                                                                </pre>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* ---------- FOOTER ---------- */}
                                <div className="modal-actions">
                                    <button className="btn btn-outline" onClick={closeAudit}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )
                }

            </div>

            <style>{`

            .secondaryBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        color: "#111827",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
        whiteSpace: "nowrap",
    },
.cmp-page {
    padding: 22px;
    background: #f7f7fb;
    min-height: 100vh;
    font-family: Inter, system-ui, Arial;
    color: #111827;
}

.cmp-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 14px;
    margin-bottom: 12px;
}

.cmp-header h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 0.2px;
}

.cmp-header p {
    margin: 6px 0 0;
    color: #6b7280;
    font-size: 13px;
}

.cmp-header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.flash {
    margin: 12px 0;
    padding: 10px 12px;
    border-radius: 12px;
    font-weight: 800;
    font-size: 13px;
}

.flash-ok {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #065f46;
}

.flash-err {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
}

.card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.card-title {
    font-size: 15px;
    font-weight: 900;
    margin-bottom: 10px;
}

.card-top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin-bottom: 10px;
}

.grid-4 {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    gap: 12px;
    align-items: end;
}

@media (max-width: 980px) {
    .grid-4 {
        grid-template-columns: 1fr;
    }
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field.end {
    align-items: flex-end;
}

.field label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 900;
}

.field input,
.field select {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 10px 12px;
    background: #fff;
    outline: none;
    font-size: 14px;
}

.hint {
    font-size: 12px;
    color: #6b7280;
}

.btn {
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 10px 14px;
    border-radius: 12px;
    font-weight: 900;
    cursor: pointer;
}

.btn:hover {
    filter: brightness(0.98);
}

.btn-primary {
    border-color: #2563eb;
    background: #2563eb;
    color: #fff;
}

.btn-outline {
    background: #fff;
}

.btn-sm {
    padding: 7px 10px;
    border-radius: 10px;
    font-size: 12px;
}

.btn-danger {
    border-color: #fca5a5;
    background: #fff;
    color: #991b1b;
}

.result {
    margin-top: 12px;
    border: 1px dashed #e5e7eb;
    border-radius: 12px;
    padding: 12px;
    background: #fafafa;
}

.result-title {
    font-size: 12px;
    font-weight: 900;
    margin-bottom: 8px;
}

.result-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(130px, 1fr));
    gap: 10px;
}

@media (max-width: 980px) {
    .result-grid {
        grid-template-columns: 1fr 1fr;
    }
}

.kv {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid #eee;
    border-radius: 10px;
    background: #fff;
    font-size: 12px;
}

.errors {
    margin-top: 10px;
    border: 1px solid #fecaca;
    background: #fff5f5;
    border-radius: 12px;
    padding: 10px;
}

.err-item {
    font-size: 12px;
    font-weight: 800;
    color: #991b1b;
    padding: 6px 0;
    border-bottom: 1px solid rgba(153, 27, 27, 0.15);
}

.err-item:last-child {
    border-bottom: none;
}

.ok {
    margin-top: 10px;
    font-size: 12px;
    font-weight: 900;
    color: #065f46;
}

.table-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.search {
    width: 340px;
    max-width: 50vw;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 10px 12px;
    outline: none;
    font-size: 14px;
}

.table-wrap {
    overflow-x: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th {
    text-align: left;
    font-size: 12px;
    color: #6b7280;
    font-weight: 900;
    padding: 10px 10px;
    border-bottom: 1px solid #e5e7eb;
    background: #fcfcff;
    white-space: nowrap;
}

td {
    padding: 10px 10px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 13px;
    white-space: nowrap;
}

td.actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.muted {
    color: #6b7280;
    font-weight: 800;
}

.note {
    margin-top: 10px;
    font-size: 12px;
    color: #6b7280;
}

/* ---------- modal ---------- */
/* ===== MODAL ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
}

.ep-modal {
  background: #fff;
  width: min(900px, 96vw);
  max-height: 90vh;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.modal-title {
  font-size: 20px;
  font-weight: 900;
}

.modal-sub {
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}

.modal-body {
  overflow: auto;
  padding-right: 6px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  font-weight: 800;
  color: #6b7280;
}

.field input {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.preview {
  margin-top: 14px;
  background: #fafafa;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  padding: 12px;
}

.preview-title {
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 6px;
}

.preview pre {
  margin: 0;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.modal-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.icon-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 6px 10px;
  font-weight: 900;
  cursor: pointer;
}

        `}</style>
        </>

    );


}



// // src/pages/centers/CenterManagementPage.js
// import React, { useEffect, useMemo, useState } from "react";
// import * as XLSX from "xlsx";
// import { createPortal } from "react-dom";

// // import "./CenterManagementPage.css";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function CenterManagementPage() {
//     const token = localStorage.getItem("token");

//     // ---------- common ----------
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//         }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     const safeJson = async (res) => {
//         const text = await res.text();
//         try {
//             return text ? JSON.parse(text) : null;
//         } catch {
//             return text;
//         }
//     };

//     // ---------- upload ----------
//     const [file, setFile] = useState(null);
//     const [actor, setActor] = useState("Admin");
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);

//     // ---------- table ----------
//     const [centers, setCenters] = useState([]);
//     const [loadingCenters, setLoadingCenters] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(false);
//     const [visibleCount, setVisibleCount] = useState(100);
//     const [search, setSearch] = useState("");

//     // ---------- edit (PATCH single) ----------
//     const [editingCenter, setEditingCenter] = useState(null); // full row (from table)
//     const [editForm, setEditForm] = useState({});
//     const [saving, setSaving] = useState(false);

//     // ---------- audits ----------
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);
//     const [auditLoading, setAuditLoading] = useState(false);
//     const [auditCenterId, setAuditCenterId] = useState("");

//     // Initial load — first 100 rows only (fast)
//     const fetchFirst100 = async () => {
//         setLoadingCenters(true);
//         setAllLoaded(false);
//         try {
//             const res = await fetch(`${BASE_URL}/api/centers?page=0&size=100`, { headers: authHeaders });
//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Failed to load centers");
//             setCenters(Array.isArray(data?.content) ? data.content : []);
//         } catch (e) {
//             flash("err", e.message);
//             setCenters([]);
//         } finally {
//             setLoadingCenters(false);
//         }
//     };

//     // Load all centers at once (triggered by "Load All" button or after mutations)
//     const fetchAllCenters = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/centers/all`, { headers: authHeaders });
//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Failed to load centers");
//             const all = Array.isArray(data) ? data : [];
//             setCenters(all);
//             setAllLoaded(true);
//             setVisibleCount(all.length);
//         } catch (e) {
//             flash("err", e.message);
//         } finally {
//             setLoadingAll(false);
//         }
//     };

//     useEffect(() => {
//         fetchFirst100();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // ------------------- Upload Excel (client parse → JSON chunks) -------------------
//     const CHUNK_SIZE = 500;
//     const PARALLEL = 3;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Please choose an Excel file first");

//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1);
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }

//         if (!dataRows.length) return flash("err", "Excel file is empty");

//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

//         let totalInserted = 0, totalSkipped = 0, failed = false, firstError = "";

//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
//             const res = await fetch(
//                 `${BASE_URL}/api/centers/upload-json?actor=${encodeURIComponent(actor || "Admin")}`,
//                 {
//                     method: "POST",
//                     headers: { ...authHeaders, "Content-Type": "application/json" },
//                     body: JSON.stringify(chunkRows),
//                 }
//             );
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const data = await res.json();
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

//             const inserted = data?.inserted ?? 0;
//             const skipped = data?.skipped ?? 0;
//             const skippedRows = data?.skippedRows ?? [];

//             console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${inserted} | Skipped: ${skipped}`);
//             if (skippedRows.length > 0) {
//                 console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
//                 skippedRows.forEach(r => console.log(r));
//                 console.groupEnd();
//             }
//             return { inserted, skipped };
//         };

//         let uploadedSoFar = 0;
//         for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
//             const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

//             const results = await Promise.allSettled(indices.map(uploadOneChunk));
//             for (const r of results) {
//                 if (r.status === "fulfilled") {
//                     totalInserted += r.value.inserted;
//                     totalSkipped += r.value.skipped;
//                     uploadedSoFar += r.value.inserted + r.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = r.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
//         }

//         setUploading(false);
//         if (!failed) {
//             console.log(`[Upload Summary] Inserted: ${totalInserted} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
//             setFile(null);
//             setUploadProgress(null);
//             fetchAllCenters();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     // ------------------- Download Table Excel (local export) -------------------
//     const [exporting, setExporting] = useState(false);

//     const downloadTableExcel = async () => {
//         if (exporting) return;
//         setExporting(true);
//         try {
//             let exportData = centers;
//             if (!allLoaded) {
//                 const res = await fetch(`${BASE_URL}/api/centers/all`, { headers: authHeaders });
//                 exportData = await safeJson(res);
//                 if (Array.isArray(exportData)) {
//                     setCenters(exportData);
//                     setAllLoaded(true);
//                     setVisibleCount(exportData.length);
//                 }
//             }
//             if (!exportData.length) { flash("err", "No centers to export"); return; }
//             const rows = exportData.map((c) => {
//                 const out = {};
//                 if (c.centerId !== undefined) out.centerId = c.centerId;
//                 Object.keys(c).forEach((k) => {
//                     if (k === "centerId") return;
//                     out[k] = typeof c[k] === "object" ? JSON.stringify(c[k]) : c[k];
//                 });
//                 return out;
//             });
//             const ws = XLSX.utils.json_to_sheet(rows);
//             const wb = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(wb, ws, "Centers");
//             XLSX.writeFile(wb, "centers.xlsx");
//         } finally {
//             setExporting(false);
//         }
//     };

//     // ------------------- Edit / Patch Single -------------------
//     const openEdit = (row) => {
//         if (!row?.centerId) {
//             flash("err", "centerId missing");
//             return;
//         }

//         setEditingCenter(row);

//         setEditForm({
//             projectName: row.projectName ?? "",

//             trainingCentreName: row.trainingCentreName ?? "",
//             centerType: row.centerType ?? "",
//             sidhCenterId: row.sidhCenterId ?? "",

//             address: row.address ?? "",
//             nearbyLandmark: row.nearbyLandmark ?? "",
//             district: row.district ?? "",
//             state: row.state ?? "",
//             pincode: row.pincode ?? "",
//             geoLocation: row.geoLocation ?? "",

//             areaSqft: row.areaSqft ?? "",
//             totalClassroom: row.totalClassroom ?? "",
//             totalLabs: row.totalLabs ?? "",
//             itLab: row.itLab ?? "",

//             maleWashroom: row.maleWashroom ?? "",
//             femaleWashroom: row.femaleWashroom ?? "",
//             unisexWashroom: row.unisexWashroom ?? "",
//             receptionArea: row.receptionArea ?? "",

//             spocName: row.spocName ?? "",
//             spocMobile: row.spocMobile ?? "",
//             spocEmail: row.spocEmail ?? "",

//             addressProofDocument: row.addressProofDocument ?? "",
//         });
//     };


//     const patchCenter = async () => {
//         if (!editingCenter?.centerId) return;

//         setSaving(true);
//         try {
//             const qs = new URLSearchParams({
//                 centerId: editingCenter.centerId,
//                 actor: actor || "SYSTEM",
//             }).toString();

//             const payload = { ...editForm };

//             // convert numeric fields safely
//             ["totalClassroom", "maleWashroom"].forEach((k) => {
//                 if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
//                     delete payload[k];
//                     return;
//                 }
//                 const n = Number(payload[k]);
//                 if (!Number.isNaN(n)) payload[k] = n;
//             });

//             // remove empty strings (treat as not provided)
//             Object.keys(payload).forEach((k) => {
//                 if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
//                     delete payload[k];
//                 }
//             });

//             const res = await fetch(`${BASE_URL}/api/centers?${qs}`, {
//                 method: "PATCH",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(payload),
//             });

//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Patch failed");

//             flash("ok", "Center updated");
//             setEditingCenter(null);
//             setEditForm({});
//             fetchAllCenters();
//         } catch (e) {
//             flash("err", e.message);
//         } finally {
//             setSaving(false);
//         }
//     };

//     const FormField = ({ label, value, onChange, type = "text", placeholder }) => (
//         <div className="field">
//             <label>{label}</label>
//             <input
//                 type={type}
//                 value={value ?? ""}
//                 placeholder={placeholder}
//                 onChange={(e) => onChange(e.target.value)}
//             />
//         </div>
//     );


//     // ------------------- Soft Delete -------------------
//     const deactivateCenter = async (centerId) => {
//         if (!centerId) return;
//         if (!window.confirm(`Deactivate center ${centerId}?`)) return;

//         try {
//             const qs = new URLSearchParams({
//                 actor: actor || "SYSTEM",
//             }).toString();

//             const res = await fetch(`${BASE_URL}/api/centers?centerId=${encodeURIComponent(centerId)}&${qs}`, {

//                 method: "DELETE",
//                 headers: authHeaders,
//             });

//             if (res.status === 204) {
//                 flash("ok", "Center deactivated");
//                 fetchAllCenters();
//                 return;
//             }

//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Delete failed");
//             flash("ok", "Center deactivated");
//             fetchAllCenters();
//         } catch (e) {
//             flash("err", e.message);
//         }
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/center_creation_template.xlsx"; // local path
//         a.download = "center_creation_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     // ------------------- Audits -------------------
//     const openAuditsAll = async () => {
//         setAuditOpen(true);
//         setAuditCenterId("");
//         setAuditLoading(true);
//         setAuditRows([]);
//         try {
//             const res = await fetch(`${BASE_URL}/api/centers/audits`, { headers: authHeaders });
//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Failed to load audits");
//             setAuditRows(Array.isArray(data) ? data : []);
//         } catch (e) {
//             flash("err", e.message);
//         } finally {
//             setAuditLoading(false);
//         }
//     };

//     const openAuditsByCenter = async (centerId) => {
//         console.log(centerId)
//         setAuditOpen(true);
//         setAuditCenterId(centerId);
//         setAuditLoading(true);
//         setAuditRows([]);
//         try {
//             const res = await fetch(
//                 `${BASE_URL}/api/centers/audits/by-center?centerId=${encodeURIComponent(centerId)}`,

//                 { headers: authHeaders }
//             );
//             const data = await safeJson(res);
//             if (!res.ok) throw new Error(data?.message || "Failed to load audits");
//             setAuditRows(Array.isArray(data) ? data : []);
//         } catch (e) {
//             flash("err", e.message);
//         } finally {
//             setAuditLoading(false);
//         }
//     };

//     const closeAudit = () => {
//         setAuditOpen(false);
//         setAuditRows([]);
//         setAuditCenterId("");
//     };

//     // ------------------- Search & Columns -------------------
//     const filtered = useMemo(() => {
//         const q = search.trim().toLowerCase();
//         if (!q) return centers;
//         return centers.filter((c) => {
//             const id = String(c.centerId || "").toLowerCase();
//             const name = String(c.trainingCentreName || c.centerName || "").toLowerCase();
//             const proj = String(c.projectName || "").toLowerCase();
//             return id.includes(q) || name.includes(q) || proj.includes(q);
//         });
//     }, [centers, search]);

//     const columns = useMemo(() => {
//         const sample = filtered[0] || centers[0];
//         if (!sample) return [];
//         const keys = Object.keys(sample);
//         const priority = ["centerId"];
//         const rest = keys.filter((k) => !priority.includes(k));
//         return [...priority.filter((k) => keys.includes(k)), ...rest];
//     }, [filtered, centers]);

//     return (
//         <>
//             <div className="cmp-page">
//                 <div className="cmp-header">
//                     <div>
//                         <h1>Center Management</h1>
//                         {/* <p>
//                             Excel UPSERT/UPDATE_ONLY, Single PATCH update, Soft delete, Audit logs
//                         </p> */}
//                     </div>
//                     <div className="cmp-header-actions mt-1">
//                         <button className="btn" onClick={fetchFirst100} disabled={loadingCenters}>
//                             {loadingCenters ? "Refreshing..." : "Refresh"}
//                         </button>
//                         {/* <button className="btn btn-outline" onClick={openAuditsAll}>
//                             View All Audits
//                         </button> */}
//                     </div>
//                 </div>

//                 {msg.text ? (
//                     <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                         <span>{msg.text}</span>
//                         <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                     </div>
//                 ) : null}

//                 {/* Upload progress bar */}
//                 {uploadProgress && (
//                     <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                             <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                                 {uploadProgress.error
//                                     ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                     : uploading
//                                         ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                         : "Upload complete"}
//                             </span>
//                             <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                                 {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                                 &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                             </span>
//                         </div>
//                         <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                             <div style={{
//                                 height: "100%",
//                                 width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                                 background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                                 borderRadius: 999,
//                                 transition: "width 0.3s ease",
//                             }} />
//                         </div>
//                         <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                             {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                         </div>
//                     </div>
//                 )}

//                 {/* Upload Card */}
//                 <div className="card">
//                     <div className="card-title">1️⃣ Excel Upload</div>

//                     <div className="grid-4">
//                         <div className="field">
//                             <label>Excel File *</label>
//                             <input
//                                 type="file"
//                                 accept=".xlsx,.xls"
//                                 onChange={(e) => setFile(e.target.files?.[0] || null)}
//                             />
//                             <div className="hint">{file ? `Selected: ${file.name}` : "Choose .xlsx/.xls"}</div>
//                         </div>
//                         {/*
//                         <div className="field">
//                             <label>Mode</label>
//                             <select value={mode} onChange={(e) => setMode(e.target.value)}>
//                                 <option value="UPSERT">UPSERT (Create + Update)</option>
//                                 <option value="UPDATE_ONLY">UPDATE_ONLY (Only update existing)</option>
//                             </select>
//                             <div className="hint">Sent as query param: mode</div>
//                         </div> */}

//                         {/* <div className="field" >
//                             <label>createdBy</label>
//                             <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Admin" />
//                             <div className="hint">.</div>
//                         </div> */}

//                         <div className="field end" style={{ marginBottom: "30px" }}>
//                             <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                                 {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                             </button>
//                         </div>
//                     </div>

//                     <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", marginTop: "1rem" }}>
//                         <button className="btn btn-outline" onClick={downloadTemplate}>
//                             Download Excel Template
//                         </button>

//                     </div>

//                     {/* {uploadResult ? (
//                         <div className="result">
//                             <div className="result-title">Upload Result</div>

//                             <div className="result-grid">
//                                 <div className="kv"><span>Total Rows</span><b>{uploadResult.totalRows ?? 0}</b></div>
//                                 <div className="kv"><span>Created</span><b>{uploadResult.created ?? 0}</b></div>
//                                 <div className="kv"><span>Updated</span><b>{uploadResult.updated ?? 0}</b></div>
//                                 <div className="kv"><span>Skipped</span><b>{uploadResult.skipped ?? 0}</b></div>
//                                 <div className="kv"><span>Failed</span><b>{uploadResult.failed ?? 0}</b></div>
//                             </div>

//                             {Array.isArray(uploadResult.errors) && uploadResult.errors.length ? (
//                                 <div className="errors">
//                                     <div className="result-title">Row Errors</div>
//                                     {uploadResult.errors.map((er, i) => (
//                                         <div className="err-item" key={i}>
//                                             Row {er.rowNumber}: {er.message}
//                                         </div>
//                                     ))}
//                                 </div>
//                             ) : (
//                                 <div className="ok">No row errors ✅</div>
//                             )}
//                         </div>
//                     ) : null} */}
//                 </div>

//                 {/* Table Card */}
//                 <div className="card">
//                     <div className="card-top">
//                         <div className="card-title">2️⃣ Centers Table</div>
//                         <div className="table-actions">
//                             <input
//                                 className="search"
//                                 value={search}
//                                 onChange={(e) => setSearch(e.target.value)}
//                                 placeholder="Search by centerId / name / project / department..."
//                             />
//                             <button className="btn btn-outline" onClick={downloadTableExcel} disabled={!centers.length || exporting}>
//                                 {exporting ? "Processing..." : "Export Excel"}
//                             </button>
//                         </div>
//                     </div>

//                     <div className="table-wrap">
//                         <table>
//                             <thead>
//                                 <tr>
//                                     <th>#</th>
//                                     {columns.length ? columns.map((c) => <th key={c}>{c}</th>) : <th>Centers</th>}
//                                     <th>Actions</th>
//                                 </tr>
//                             </thead>

//                             <tbody>
//                                 {filtered.length === 0 ? (
//                                     <tr>
//                                         <td colSpan={Math.max(columns.length + 2, 3)} className="muted">
//                                             {loadingCenters ? "Loading..." : "No centers found"}
//                                         </td>
//                                     </tr>
//                                 ) : (
//                                     filtered.slice(0, visibleCount).map((c, idx) => (
//                                         <tr key={c.centerId || idx}>
//                                             <td>{idx + 1}</td>
//                                             {columns.map((k) => (
//                                                 <td key={k}>
//                                                     {typeof c[k] === "object" ? JSON.stringify(c[k]) : String(c[k] ?? "")}
//                                                 </td>
//                                             ))}
//                                             <td className="actions">
//                                                 <button className="btn btn-sm btn-danger" onClick={() => deactivateCenter(c.centerId)}>
//                                                     Deactivate
//                                                 </button>
//                                             </td>
//                                         </tr>
//                                     ))
//                                 )}
//                                 {!allLoaded && !loadingCenters && (
//                                     <tr>
//                                         <td colSpan={Math.max(columns.length + 2, 3)} style={{ textAlign: "center", padding: 14 }}>
//                                             <button className="btn btn-outline" onClick={fetchAllCenters} disabled={loadingAll}>
//                                                 {loadingAll ? "Loading all data…" : "Load All Data"}
//                                             </button>
//                                         </td>
//                                     </tr>
//                                 )}
//                                 {allLoaded && filtered.length > visibleCount && (
//                                     <tr>
//                                         <td colSpan={Math.max(columns.length + 2, 3)} style={{ textAlign: "center", padding: 14 }}>
//                                             <button className="btn btn-outline" onClick={() => setVisibleCount(c => c + 100)}>
//                                                 Show More ({filtered.length - visibleCount} remaining)
//                                             </button>
//                                         </td>
//                                     </tr>
//                                 )}
//                             </tbody>
//                         </table>
//                     </div>

//                     {/* <div className="note">
//                         <b>Note:</b> Table requires backend <code>GET /api/centers</code>. If not available, tell me and I’ll adjust page to show results only.
//                     </div> */}
//                 </div>

//                 {/* EDIT MODAL (PATCH /api/centers?centerId=...&actor=...) */}


//                 {editingCenter &&
//                     createPortal(
//                         <div className="modal-overlay" onClick={() => setEditingCenter(null)}>
//                             <div className="ep-modal" onClick={(e) => e.stopPropagation()}>

//                                 {/* ---------- HEADER ---------- */}
//                                 <div className="modal-head">
//                                     <div>
//                                         <div className="modal-title">Edit Center</div>
//                                         <div className="modal-sub">
//                                             Center ID: <b>{editingCenter.centerId}</b>
//                                         </div>
//                                     </div>
//                                     <button className="icon-btn" onClick={() => setEditingCenter(null)}>✕</button>
//                                 </div>

//                                 {/* ---------- FORM ---------- */}
//                                 <div className="modal-body">
//                                     <div className="form-grid">

//                                         {/* ---------- PROJECT ---------- */}
//                                         <FormField
//                                             label="Project Name"
//                                             value={editForm.projectName}
//                                             onChange={(v) => setEditForm(p => ({ ...p, projectName: v }))}
//                                         />

//                                         {/* ---------- CENTER BASIC ---------- */}
//                                         <FormField
//                                             label="Training Centre Name"
//                                             value={editForm.trainingCentreName}
//                                             onChange={(v) => setEditForm(p => ({ ...p, trainingCentreName: v }))}
//                                         />

//                                         <FormField
//                                             label="Center Type"
//                                             placeholder="Training / Assessment"
//                                             value={editForm.centerType}
//                                             onChange={(v) => setEditForm(p => ({ ...p, centerType: v }))}
//                                         />

//                                         <FormField
//                                             label="SIDH Center ID"
//                                             value={editForm.sidhCenterId}
//                                             onChange={(v) => setEditForm(p => ({ ...p, sidhCenterId: v }))}
//                                         />

//                                         {/* ---------- LOCATION ---------- */}
//                                         <FormField
//                                             label="Address"
//                                             value={editForm.address}
//                                             onChange={(v) => setEditForm(p => ({ ...p, address: v }))}
//                                         />

//                                         <FormField
//                                             label="Nearby Landmark"
//                                             value={editForm.nearbyLandmark}
//                                             onChange={(v) => setEditForm(p => ({ ...p, nearbyLandmark: v }))}
//                                         />

//                                         <FormField
//                                             label="District"
//                                             value={editForm.district}
//                                             onChange={(v) => setEditForm(p => ({ ...p, district: v }))}
//                                         />

//                                         <FormField
//                                             label="State"
//                                             value={editForm.state}
//                                             onChange={(v) => setEditForm(p => ({ ...p, state: v }))}
//                                         />

//                                         <FormField
//                                             label="Pincode"
//                                             value={editForm.pincode}
//                                             onChange={(v) => setEditForm(p => ({ ...p, pincode: v }))}
//                                         />

//                                         <FormField
//                                             label="Geo Location"
//                                             placeholder="lat,long"
//                                             value={editForm.geoLocation}
//                                             onChange={(v) => setEditForm(p => ({ ...p, geoLocation: v }))}
//                                         />

//                                         {/* ---------- INFRASTRUCTURE ---------- */}
//                                         <FormField
//                                             label="Area (Sqft)"
//                                             type="number"
//                                             value={editForm.areaSqft}
//                                             onChange={(v) => setEditForm(p => ({ ...p, areaSqft: v }))}
//                                         />

//                                         <FormField
//                                             label="Total Classrooms"
//                                             type="number"
//                                             value={editForm.totalClassroom}
//                                             onChange={(v) => setEditForm(p => ({ ...p, totalClassroom: v }))}
//                                         />

//                                         <FormField
//                                             label="Total Labs"
//                                             type="number"
//                                             value={editForm.totalLabs}
//                                             onChange={(v) => setEditForm(p => ({ ...p, totalLabs: v }))}
//                                         />

//                                         <FormField
//                                             label="IT Labs"
//                                             type="number"
//                                             value={editForm.itLab}
//                                             onChange={(v) => setEditForm(p => ({ ...p, itLab: v }))}
//                                         />

//                                         {/* ---------- WASHROOM ---------- */}
//                                         <FormField
//                                             label="Male Washroom"
//                                             type="number"
//                                             value={editForm.maleWashroom}
//                                             onChange={(v) => setEditForm(p => ({ ...p, maleWashroom: v }))}
//                                         />

//                                         <FormField
//                                             label="Female Washroom"
//                                             type="number"
//                                             value={editForm.femaleWashroom}
//                                             onChange={(v) => setEditForm(p => ({ ...p, femaleWashroom: v }))}
//                                         />

//                                         <FormField
//                                             label="Unisex Washroom"
//                                             type="number"
//                                             value={editForm.unisexWashroom}
//                                             onChange={(v) => setEditForm(p => ({ ...p, unisexWashroom: v }))}
//                                         />

//                                         <FormField
//                                             label="Reception Area"
//                                             placeholder="YES / NO"
//                                             value={editForm.receptionArea}
//                                             onChange={(v) => setEditForm(p => ({ ...p, receptionArea: v }))}
//                                         />

//                                         {/* ---------- SPOC ---------- */}
//                                         <FormField
//                                             label="SPOC Name"
//                                             value={editForm.spocName}
//                                             onChange={(v) => setEditForm(p => ({ ...p, spocName: v }))}
//                                         />

//                                         <FormField
//                                             label="SPOC Mobile"
//                                             value={editForm.spocMobile}
//                                             onChange={(v) => setEditForm(p => ({ ...p, spocMobile: v }))}
//                                         />

//                                         <FormField
//                                             label="SPOC Email"
//                                             type="email"
//                                             value={editForm.spocEmail}
//                                             onChange={(v) => setEditForm(p => ({ ...p, spocEmail: v }))}
//                                         />

//                                         {/* ---------- DOCUMENT ---------- */}
//                                         <FormField
//                                             label="Address Proof Document"
//                                             value={editForm.addressProofDocument}
//                                             onChange={(v) => setEditForm(p => ({ ...p, addressProofDocument: v }))}
//                                         />

//                                     </div>


//                                     {/* ---------- PAYLOAD PREVIEW ---------- */}
//                                     {/* <div className="preview">
//                                         <div className="preview-title">PATCH Payload Preview</div>
//                                         <pre>{JSON.stringify(editForm, null, 2)}</pre>
//                                     </div> */}
//                                 </div>

//                                 {/* ---------- FOOTER ---------- */}
//                                 <div className="modal-actions">
//                                     <button className="btn btn-outline" onClick={() => setEditingCenter(null)}>
//                                         Cancel
//                                     </button>
//                                     <button className="btn btn-primary" onClick={patchCenter} disabled={saving}>
//                                         {saving ? "Saving..." : "Save Changes"}
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>,
//                         document.body
//                     )
//                 }




//                 {auditOpen &&
//                     createPortal(
//                         <div className="modal-overlay" onClick={closeAudit}>
//                             <div
//                                 className="ep-modal ep-modal-wide"
//                                 onClick={(e) => e.stopPropagation()}
//                             >
//                                 {/* ---------- HEADER ---------- */}
//                                 <div className="modal-head">
//                                     <div>
//                                         <div className="modal-title">Center Audit Logs</div>
//                                         <div className="modal-sub">
//                                             {auditCenterId ? (
//                                                 <>Center ID: <b>{auditCenterId}</b></>
//                                             ) : (
//                                                 <>All Audits</>
//                                             )}
//                                         </div>
//                                     </div>
//                                     <button className="icon-btn" onClick={closeAudit}>✕</button>
//                                 </div>

//                                 {/* ---------- BODY ---------- */}
//                                 <div className="modal-body">
//                                     {auditLoading ? (
//                                         <div className="muted">Loading audits...</div>
//                                     ) : auditRows.length === 0 ? (
//                                         <div className="muted">No audit logs found</div>
//                                     ) : (
//                                         <div className="table-wrap">
//                                             <table>
//                                                 <thead>
//                                                     <tr>
//                                                         <th>ID</th>
//                                                         <th>Action</th>
//                                                         <th>Method</th>
//                                                         <th>Actor</th>
//                                                         <th>Center</th>
//                                                         <th>Created At</th>
//                                                         <th>Before</th>
//                                                         <th>After</th>
//                                                     </tr>
//                                                 </thead>
//                                                 <tbody>
//                                                     {auditRows.map((a) => (
//                                                         <tr key={a.id || `${a.centerId}-${a.createdAt}`}>
//                                                             <td>{a.id ?? "-"}</td>
//                                                             <td>{a.action ?? "-"}</td>
//                                                             <td>{a.method ?? "-"}</td>
//                                                             <td>{a.actor ?? "-"}</td>
//                                                             <td>{a.centerId ?? "-"}</td>
//                                                             <td>{a.createdAt ?? "-"}</td>
//                                                             <td>
//                                                                 <pre className="pre-small">
//                                                                     {String(a.beforeJson ?? "")}
//                                                                 </pre>
//                                                             </td>
//                                                             <td>
//                                                                 <pre className="pre-small">
//                                                                     {String(a.afterJson ?? "")}
//                                                                 </pre>
//                                                             </td>
//                                                         </tr>
//                                                     ))}
//                                                 </tbody>
//                                             </table>
//                                         </div>
//                                     )}
//                                 </div>

//                                 {/* ---------- FOOTER ---------- */}
//                                 <div className="modal-actions">
//                                     <button className="btn btn-outline" onClick={closeAudit}>
//                                         Close
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>,
//                         document.body
//                     )
//                 }

//             </div>

//             <style>{`

//             .secondaryBtn: {
//         border: "1px solid #e5e7eb",
//         background: "#fff",
//         color: "#111827",
//         padding: "10px 14px",
//         borderRadius: 10,
//         cursor: "pointer",
//         fontWeight: 900,
//         whiteSpace: "nowrap",
//     },
// .cmp-page {
//     padding: 22px;
//     background: #f7f7fb;
//     min-height: 100vh;
//     font-family: Inter, system-ui, Arial;
//     color: #111827;
// }

// .cmp-header {
//     display: flex;
//     justify-content: space-between;
//     align-items: flex-end;
//     gap: 14px;
//     margin-bottom: 12px;
// }

// .cmp-header h1 {
//     margin: 0;
//     font-size: 26px;
//     font-weight: 900;
//     letter-spacing: 0.2px;
// }

// .cmp-header p {
//     margin: 6px 0 0;
//     color: #6b7280;
//     font-size: 13px;
// }

// .cmp-header-actions {
//     display: flex;
//     gap: 10px;
//     align-items: center;
// }

// .flash {
//     margin: 12px 0;
//     padding: 10px 12px;
//     border-radius: 12px;
//     font-weight: 800;
//     font-size: 13px;
// }

// .flash-ok {
//     background: #ecfdf5;
//     border: 1px solid #a7f3d0;
//     color: #065f46;
// }

// .flash-err {
//     background: #fef2f2;
//     border: 1px solid #fecaca;
//     color: #991b1b;
// }

// .card {
//     background: #fff;
//     border: 1px solid #e5e7eb;
//     border-radius: 14px;
//     padding: 16px;
//     margin-bottom: 14px;
//     box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
// }

// .card-title {
//     font-size: 15px;
//     font-weight: 900;
//     margin-bottom: 10px;
// }

// .card-top {
//     display: flex;
//     justify-content: space-between;
//     gap: 12px;
//     align-items: center;
//     margin-bottom: 10px;
// }

// .grid-4 {
//     display: grid;
//     grid-template-columns: 1.6fr 1fr;
//     gap: 12px;
//     align-items: end;
// }

// @media (max-width: 980px) {
//     .grid-4 {
//         grid-template-columns: 1fr;
//     }
// }

// .field {
//     display: flex;
//     flex-direction: column;
//     gap: 6px;
// }

// .field.end {
//     align-items: flex-end;
// }

// .field label {
//     font-size: 12px;
//     color: #6b7280;
//     font-weight: 900;
// }

// .field input,
// .field select {
//     border: 1px solid #e5e7eb;
//     border-radius: 12px;
//     padding: 10px 12px;
//     background: #fff;
//     outline: none;
//     font-size: 14px;
// }

// .hint {
//     font-size: 12px;
//     color: #6b7280;
// }

// .btn {
//     border: 1px solid #e5e7eb;
//     background: #fff;
//     padding: 10px 14px;
//     border-radius: 12px;
//     font-weight: 900;
//     cursor: pointer;
// }

// .btn:hover {
//     filter: brightness(0.98);
// }

// .btn-primary {
//     border-color: #2563eb;
//     background: #2563eb;
//     color: #fff;
// }

// .btn-outline {
//     background: #fff;
// }

// .btn-sm {
//     padding: 7px 10px;
//     border-radius: 10px;
//     font-size: 12px;
// }

// .btn-danger {
//     border-color: #fca5a5;
//     background: #fff;
//     color: #991b1b;
// }

// .result {
//     margin-top: 12px;
//     border: 1px dashed #e5e7eb;
//     border-radius: 12px;
//     padding: 12px;
//     background: #fafafa;
// }

// .result-title {
//     font-size: 12px;
//     font-weight: 900;
//     margin-bottom: 8px;
// }

// .result-grid {
//     display: grid;
//     grid-template-columns: repeat(5, minmax(130px, 1fr));
//     gap: 10px;
// }

// @media (max-width: 980px) {
//     .result-grid {
//         grid-template-columns: 1fr 1fr;
//     }
// }

// .kv {
//     display: flex;
//     justify-content: space-between;
//     gap: 10px;
//     padding: 8px 10px;
//     border: 1px solid #eee;
//     border-radius: 10px;
//     background: #fff;
//     font-size: 12px;
// }

// .errors {
//     margin-top: 10px;
//     border: 1px solid #fecaca;
//     background: #fff5f5;
//     border-radius: 12px;
//     padding: 10px;
// }

// .err-item {
//     font-size: 12px;
//     font-weight: 800;
//     color: #991b1b;
//     padding: 6px 0;
//     border-bottom: 1px solid rgba(153, 27, 27, 0.15);
// }

// .err-item:last-child {
//     border-bottom: none;
// }

// .ok {
//     margin-top: 10px;
//     font-size: 12px;
//     font-weight: 900;
//     color: #065f46;
// }

// .table-actions {
//     display: flex;
//     gap: 10px;
//     align-items: center;
// }

// .search {
//     width: 340px;
//     max-width: 50vw;
//     border: 1px solid #e5e7eb;
//     border-radius: 12px;
//     padding: 10px 12px;
//     outline: none;
//     font-size: 14px;
// }

// .table-wrap {
//     overflow-x: auto;
// }

// table {
//     width: 100%;
//     border-collapse: collapse;
// }

// th {
//     text-align: left;
//     font-size: 12px;
//     color: #6b7280;
//     font-weight: 900;
//     padding: 10px 10px;
//     border-bottom: 1px solid #e5e7eb;
//     background: #fcfcff;
//     white-space: nowrap;
// }

// td {
//     padding: 10px 10px;
//     border-bottom: 1px solid #f1f5f9;
//     font-size: 13px;
//     white-space: nowrap;
// }

// td.actions {
//     display: flex;
//     gap: 8px;
//     align-items: center;
// }

// .muted {
//     color: #6b7280;
//     font-weight: 800;
// }

// .note {
//     margin-top: 10px;
//     font-size: 12px;
//     color: #6b7280;
// }

// /* ---------- modal ---------- */
// /* ===== MODAL ===== */
// .modal-overlay {
//   position: fixed;
//   inset: 0;
//   background: rgba(0, 0, 0, 0.45);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   z-index: 2147483647;
// }

// .ep-modal {
//   background: #fff;
//   width: min(900px, 96vw);
//   max-height: 90vh;
//   border-radius: 16px;
//   padding: 18px;
//   box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
//   display: flex;
//   flex-direction: column;
// }

// .modal-head {
//   display: flex;
//   justify-content: space-between;
//   align-items: flex-start;
//   margin-bottom: 12px;
// }

// .modal-title {
//   font-size: 20px;
//   font-weight: 900;
// }

// .modal-sub {
//   font-size: 12px;
//   color: #6b7280;
//   font-weight: 700;
// }

// .modal-body {
//   overflow: auto;
//   padding-right: 6px;
// }

// .form-grid {
//   display: grid;
//   grid-template-columns: repeat(2, minmax(0, 1fr));
//   gap: 14px;
// }

// @media (max-width: 768px) {
//   .form-grid {
//     grid-template-columns: 1fr;
//   }
// }

// .field {
//   display: flex;
//   flex-direction: column;
//   gap: 6px;
// }

// .field label {
//   font-size: 12px;
//   font-weight: 800;
//   color: #6b7280;
// }

// .field input {
//   padding: 10px 12px;
//   border-radius: 10px;
//   border: 1px solid #e5e7eb;
//   font-size: 14px;
// }

// .preview {
//   margin-top: 14px;
//   background: #fafafa;
//   border: 1px dashed #e5e7eb;
//   border-radius: 12px;
//   padding: 12px;
// }

// .preview-title {
//   font-size: 12px;
//   font-weight: 900;
//   margin-bottom: 6px;
// }

// .preview pre {
//   margin: 0;
//   font-size: 12px;
//   white-space: pre-wrap;
//   word-break: break-word;
// }

// .modal-actions {
//   margin-top: 14px;
//   display: flex;
//   justify-content: flex-end;
//   gap: 10px;
// }

// .icon-btn {
//   border: 1px solid #e5e7eb;
//   background: #fff;
//   border-radius: 10px;
//   padding: 6px 10px;
//   font-weight: 900;
//   cursor: pointer;
// }

//         `}</style>
//         </>

//     );


// }





// // // src/pages/centers/CenterManagementPage.js
// // import React, { useEffect, useMemo, useState } from "react";
// // import * as XLSX from "xlsx";
// // import { createPortal } from "react-dom";

// // // import "./CenterManagementPage.css";

// // const BASE_URL = "https://mis.empowerplus.in";

// // export default function CenterManagementPage() {
// //     const token = localStorage.getItem("token");

// //     // ---------- common ----------
// //     const [msg, setMsg] = useState({ type: "", text: "" });
// //     const flash = (type, text) => {
// //         setMsg({ type, text });
// //     };

// //     const authHeaders = useMemo(
// //         () => ({
// //             Authorization: `Bearer ${token}`,
// //         }),
// //         [token]
// //     );

// //     const jsonHeaders = useMemo(
// //         () => ({
// //             Authorization: `Bearer ${token}`,
// //             "Content-Type": "application/json",
// //         }),
// //         [token]
// //     );

// //     const safeJson = async (res) => {
// //         const text = await res.text();
// //         try {
// //             return text ? JSON.parse(text) : null;
// //         } catch {
// //             return text;
// //         }
// //     };

// //     // ---------- upload ----------
// //     const [file, setFile] = useState(null);
// //     const [mode, setMode] = useState("UPSERT"); // UPSERT | UPDATE_ONLY
// //     const [actor, setActor] = useState("Admin");
// //     const [uploading, setUploading] = useState(false);
// //     const [uploadResult, setUploadResult] = useState(null);

// //     // ---------- table ----------
// //     const [centers, setCenters] = useState([]);
// //     const [loadingCenters, setLoadingCenters] = useState(false);
// //     const [search, setSearch] = useState("");

// //     // ---------- edit (PATCH single) ----------
// //     const [editingCenter, setEditingCenter] = useState(null); // full row (from table)
// //     const [editForm, setEditForm] = useState({});
// //     const [saving, setSaving] = useState(false);

// //     // ---------- audits ----------
// //     const [auditOpen, setAuditOpen] = useState(false);
// //     const [auditRows, setAuditRows] = useState([]);
// //     const [auditLoading, setAuditLoading] = useState(false);
// //     const [auditCenterId, setAuditCenterId] = useState("");

// //     // ------------------- OPTIONAL: if you have a GET centers endpoint, set it here -------------------
// //     // If you DON'T have it yet, table will stay empty. Upload results + audit will still work.
// //     const LIST_URL = `${BASE_URL}/api/centers`; // <-- ensure backend has GET /api/centers

// //     const fetchCenters = async () => {
// //         setLoadingCenters(true);

// //         try {
// //             const res = await fetch(`${LIST_URL}?size=10000`, { headers: authHeaders });
// //             const data = await safeJson(res);

// //             if (!res.ok) throw new Error(data?.message || "Failed to load centers");

// //             // ✅ Spring Boot pagination support
// //             setCenters(Array.isArray(data?.content) ? data.content : []);

// //         } catch (e) {
// //             flash("err", e.message);
// //             setCenters([]);
// //         } finally {
// //             setLoadingCenters(false);
// //         }
// //     };

// //     useEffect(() => {
// //         // won't break if endpoint missing; just shows message
// //         fetchCenters();
// //         // eslint-disable-next-line react-hooks/exhaustive-deps
// //     }, []);

// //     // ------------------- Upload Excel (UPSERT / UPDATE_ONLY) -------------------
// //     const uploadExcel = async () => {
// //         if (!file) return flash("err", "Please choose an Excel file first");

// //         setUploading(true);
// //         setUploadResult(null);

// //         try {
// //             const qs = new URLSearchParams({
// //                 mode,
// //                 actor: actor || "SYSTEM",
// //             }).toString();

// //             const formData = new FormData();
// //             formData.append("file", file);

// //             const res = await fetch(`${BASE_URL}/api/centers/upload?${qs}`, {
// //                 method: "POST",
// //                 headers: { Authorization: `Bearer ${token}` }, // do not set multipart content-type manually
// //                 body: formData,
// //             });

// //             if (res.status === 413) {
// //                 flash("err", "File too large for server (413). Ask admin to increase nginx client_max_body_size.");
// //                 return;
// //             }
// //             const data = await safeJson(res);
// //             if (!res.ok) {
// //                 const msgText =
// //                     data?.message ||
// //                     (Array.isArray(data?.errors) && data.errors.length
// //                         ? `Row ${data.errors[0].rowNumber}: ${data.errors[0].message}`
// //                         : "Upload failed");
// //                 throw new Error(msgText);
// //             }

// //             setUploadResult(data);
// //             const cnt = data?.successCount ?? data?.inserted ?? data?.totalRows ?? data?.count ?? data?.saved ?? data?.rowsCreated ?? data?.rowsInserted ?? null;
// //             flash("ok", `Upload done (${mode})${cnt != null ? ` — ${cnt} rows` : ""}`);
// //             setFile(null);

// //             // refresh list if available
// //             fetchCenters();
// //         } catch (e) {
// //             flash("err", e.message || "Upload failed – check your connection");
// //         } finally {
// //             setUploading(false);
// //         }
// //     };

// //     // ------------------- Download Table Excel (local export) -------------------
// //     const [exporting, setExporting] = useState(false);

// //     const downloadTableExcel = () => {
// //         if (!centers.length) return flash("err", "No centers to export");
// //         if (exporting) return;
// //         setExporting(true);
// //         setTimeout(() => {
// //             try {
// //                 const rows = centers.map((c) => {
// //                     const out = {};
// //                     if (c.centerId !== undefined) out.centerId = c.centerId;
// //                     Object.keys(c).forEach((k) => {
// //                         if (k === "centerId") return;
// //                         out[k] = typeof c[k] === "object" ? JSON.stringify(c[k]) : c[k];
// //                     });
// //                     return out;
// //                 });

// //                 const ws = XLSX.utils.json_to_sheet(rows);
// //                 const wb = XLSX.utils.book_new();
// //                 XLSX.utils.book_append_sheet(wb, ws, "Centers");
// //                 XLSX.writeFile(wb, "centers.xlsx");
// //             } finally {
// //                 setExporting(false);
// //             }
// //         }, 50);
// //     };

// //     // ------------------- Edit / Patch Single -------------------
// //     const openEdit = (row) => {
// //         if (!row?.centerId) {
// //             flash("err", "centerId missing");
// //             return;
// //         }

// //         setEditingCenter(row);

// //         setEditForm({
// //             projectName: row.projectName ?? "",

// //             trainingCentreName: row.trainingCentreName ?? "",
// //             centerType: row.centerType ?? "",
// //             sidhCenterId: row.sidhCenterId ?? "",

// //             address: row.address ?? "",
// //             nearbyLandmark: row.nearbyLandmark ?? "",
// //             district: row.district ?? "",
// //             state: row.state ?? "",
// //             pincode: row.pincode ?? "",
// //             geoLocation: row.geoLocation ?? "",

// //             areaSqft: row.areaSqft ?? "",
// //             totalClassroom: row.totalClassroom ?? "",
// //             totalLabs: row.totalLabs ?? "",
// //             itLab: row.itLab ?? "",

// //             maleWashroom: row.maleWashroom ?? "",
// //             femaleWashroom: row.femaleWashroom ?? "",
// //             unisexWashroom: row.unisexWashroom ?? "",
// //             receptionArea: row.receptionArea ?? "",

// //             spocName: row.spocName ?? "",
// //             spocMobile: row.spocMobile ?? "",
// //             spocEmail: row.spocEmail ?? "",

// //             addressProofDocument: row.addressProofDocument ?? "",
// //         });
// //     };


// //     const patchCenter = async () => {
// //         if (!editingCenter?.centerId) return;

// //         setSaving(true);
// //         try {
// //             const qs = new URLSearchParams({
// //                 centerId: editingCenter.centerId,
// //                 actor: actor || "SYSTEM",
// //             }).toString();

// //             const payload = { ...editForm };

// //             // convert numeric fields safely
// //             ["totalClassroom", "maleWashroom"].forEach((k) => {
// //                 if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
// //                     delete payload[k];
// //                     return;
// //                 }
// //                 const n = Number(payload[k]);
// //                 if (!Number.isNaN(n)) payload[k] = n;
// //             });

// //             // remove empty strings (treat as not provided)
// //             Object.keys(payload).forEach((k) => {
// //                 if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
// //                     delete payload[k];
// //                 }
// //             });

// //             const res = await fetch(`${BASE_URL}/api/centers?${qs}`, {
// //                 method: "PATCH",
// //                 headers: jsonHeaders,
// //                 body: JSON.stringify(payload),
// //             });

// //             const data = await safeJson(res);
// //             if (!res.ok) throw new Error(data?.message || "Patch failed");

// //             flash("ok", "Center updated");
// //             setEditingCenter(null);
// //             setEditForm({});
// //             fetchCenters();
// //         } catch (e) {
// //             flash("err", e.message);
// //         } finally {
// //             setSaving(false);
// //         }
// //     };

// //     const FormField = ({ label, value, onChange, type = "text", placeholder }) => (
// //         <div className="field">
// //             <label>{label}</label>
// //             <input
// //                 type={type}
// //                 value={value ?? ""}
// //                 placeholder={placeholder}
// //                 onChange={(e) => onChange(e.target.value)}
// //             />
// //         </div>
// //     );


// //     // ------------------- Soft Delete -------------------
// //     const deactivateCenter = async (centerId) => {
// //         if (!centerId) return;
// //         if (!window.confirm(`Deactivate center ${centerId}?`)) return;

// //         try {
// //             const qs = new URLSearchParams({
// //                 actor: actor || "SYSTEM",
// //             }).toString();

// //             const res = await fetch(`${BASE_URL}/api/centers?centerId=${encodeURIComponent(centerId)}&${qs}`, {

// //                 method: "DELETE",
// //                 headers: authHeaders,
// //             });

// //             if (res.status === 204) {
// //                 flash("ok", "Center deactivated");
// //                 fetchCenters();
// //                 return;
// //             }

// //             const data = await safeJson(res);
// //             if (!res.ok) throw new Error(data?.message || "Delete failed");
// //             flash("ok", "Center deactivated");
// //             fetchCenters();
// //         } catch (e) {
// //             flash("err", e.message);
// //         }
// //     };

// //     const downloadTemplate = () => {
// //         const a = document.createElement("a");
// //         a.href = "/templates/center_creation_template.xlsx"; // local path
// //         a.download = "center_creation_template.xlsx";
// //         document.body.appendChild(a);
// //         a.click();
// //         a.remove();
// //     };

// //     // ------------------- Audits -------------------
// //     const openAuditsAll = async () => {
// //         setAuditOpen(true);
// //         setAuditCenterId("");
// //         setAuditLoading(true);
// //         setAuditRows([]);
// //         try {
// //             const res = await fetch(`${BASE_URL}/api/centers/audits`, { headers: authHeaders });
// //             const data = await safeJson(res);
// //             if (!res.ok) throw new Error(data?.message || "Failed to load audits");
// //             setAuditRows(Array.isArray(data) ? data : []);
// //         } catch (e) {
// //             flash("err", e.message);
// //         } finally {
// //             setAuditLoading(false);
// //         }
// //     };

// //     const openAuditsByCenter = async (centerId) => {
// //         console.log(centerId)
// //         setAuditOpen(true);
// //         setAuditCenterId(centerId);
// //         setAuditLoading(true);
// //         setAuditRows([]);
// //         try {
// //             const res = await fetch(
// //                 `${BASE_URL}/api/centers/audits/by-center?centerId=${encodeURIComponent(centerId)}`,

// //                 { headers: authHeaders }
// //             );
// //             const data = await safeJson(res);
// //             if (!res.ok) throw new Error(data?.message || "Failed to load audits");
// //             setAuditRows(Array.isArray(data) ? data : []);
// //         } catch (e) {
// //             flash("err", e.message);
// //         } finally {
// //             setAuditLoading(false);
// //         }
// //     };

// //     const closeAudit = () => {
// //         setAuditOpen(false);
// //         setAuditRows([]);
// //         setAuditCenterId("");
// //     };

// //     // ------------------- Search & Columns -------------------
// //     const filtered = useMemo(() => {
// //         const q = search.trim().toLowerCase();
// //         if (!q) return centers;
// //         return centers.filter((c) => {
// //             const id = String(c.centerId || "").toLowerCase();
// //             const name = String(c.trainingCentreName || c.centerName || "").toLowerCase();
// //             const proj = String(c.projectName || "").toLowerCase();
// //             return id.includes(q) || name.includes(q) || proj.includes(q);
// //         });
// //     }, [centers, search]);

// //     const columns = useMemo(() => {
// //         const sample = filtered[0] || centers[0];
// //         if (!sample) return [];
// //         const keys = Object.keys(sample);
// //         const priority = ["centerId"];
// //         const rest = keys.filter((k) => !priority.includes(k));
// //         return [...priority.filter((k) => keys.includes(k)), ...rest];
// //     }, [filtered, centers]);

// //     return (
// //         <>
// //             <div className="cmp-page">
// //                 <div className="cmp-header">
// //                     <div>
// //                         <h1>Center Management</h1>
// //                         {/* <p>
// //                             Excel UPSERT/UPDATE_ONLY, Single PATCH update, Soft delete, Audit logs
// //                         </p> */}
// //                     </div>
// //                     <div className="cmp-header-actions mt-1">
// //                         <button className="btn" onClick={fetchCenters} disabled={loadingCenters}>
// //                             {loadingCenters ? "Refreshing..." : "Refresh"}
// //                         </button>
// //                         {/* <button className="btn btn-outline" onClick={openAuditsAll}>
// //                             View All Audits
// //                         </button> */}
// //                     </div>
// //                 </div>

// //                 {msg.text ? (
// //                     <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
// //                         <span>{msg.text}</span>
// //                         <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
// //                     </div>
// //                 ) : null}

// //                 {/* Upload Card */}
// //                 <div className="card">
// //                     <div className="card-title">1️⃣ Excel Upload</div>

// //                     <div className="grid-4">
// //                         <div className="field">
// //                             <label>Excel File *</label>
// //                             <input
// //                                 type="file"
// //                                 accept=".xlsx,.xls"
// //                                 onChange={(e) => setFile(e.target.files?.[0] || null)}
// //                             />
// //                             <div className="hint">{file ? `Selected: ${file.name}` : "Choose .xlsx/.xls"}</div>
// //                         </div>
// //                         {/*
// //                         <div className="field">
// //                             <label>Mode</label>
// //                             <select value={mode} onChange={(e) => setMode(e.target.value)}>
// //                                 <option value="UPSERT">UPSERT (Create + Update)</option>
// //                                 <option value="UPDATE_ONLY">UPDATE_ONLY (Only update existing)</option>
// //                             </select>
// //                             <div className="hint">Sent as query param: mode</div>
// //                         </div> */}

// //                         {/* <div className="field" >
// //                             <label>createdBy</label>
// //                             <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Admin" />
// //                             <div className="hint">.</div>
// //                         </div> */}

// //                         <div className="field end" style={{ marginBottom: "30px" }}>
// //                             <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
// //                                 {uploading ? "Uploading..." : "Upload"}
// //                             </button>
// //                         </div>
// //                     </div>

// //                     <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", marginTop: "1rem" }}>
// //                         <button className="btn btn-outline" onClick={downloadTemplate}>
// //                             Download Excel Template
// //                         </button>

// //                     </div>

// //                     {/* {uploadResult ? (
// //                         <div className="result">
// //                             <div className="result-title">Upload Result</div>

// //                             <div className="result-grid">
// //                                 <div className="kv"><span>Total Rows</span><b>{uploadResult.totalRows ?? 0}</b></div>
// //                                 <div className="kv"><span>Created</span><b>{uploadResult.created ?? 0}</b></div>
// //                                 <div className="kv"><span>Updated</span><b>{uploadResult.updated ?? 0}</b></div>
// //                                 <div className="kv"><span>Skipped</span><b>{uploadResult.skipped ?? 0}</b></div>
// //                                 <div className="kv"><span>Failed</span><b>{uploadResult.failed ?? 0}</b></div>
// //                             </div>

// //                             {Array.isArray(uploadResult.errors) && uploadResult.errors.length ? (
// //                                 <div className="errors">
// //                                     <div className="result-title">Row Errors</div>
// //                                     {uploadResult.errors.map((er, i) => (
// //                                         <div className="err-item" key={i}>
// //                                             Row {er.rowNumber}: {er.message}
// //                                         </div>
// //                                     ))}
// //                                 </div>
// //                             ) : (
// //                                 <div className="ok">No row errors ✅</div>
// //                             )}
// //                         </div>
// //                     ) : null} */}
// //                 </div>

// //                 {/* Table Card */}
// //                 <div className="card">
// //                     <div className="card-top">
// //                         <div className="card-title">2️⃣ Centers Table</div>
// //                         <div className="table-actions">
// //                             <input
// //                                 className="search"
// //                                 value={search}
// //                                 onChange={(e) => setSearch(e.target.value)}
// //                                 placeholder="Search by centerId / name / project / department..."
// //                             />
// //                             <button className="btn btn-outline" onClick={downloadTableExcel} disabled={!centers.length || exporting}>
// //                                 {exporting ? "Processing..." : "Export Excel"}
// //                             </button>
// //                         </div>
// //                     </div>

// //                     <div className="table-wrap">
// //                         <table>
// //                             <thead>
// //                                 <tr>
// //                                     <th>#</th>
// //                                     {columns.length ? columns.map((c) => <th key={c}>{c}</th>) : <th>Centers</th>}
// //                                     <th>Actions</th>
// //                                 </tr>
// //                             </thead>

// //                             <tbody>
// //                                 {filtered.length === 0 ? (
// //                                     <tr>
// //                                         <td colSpan={Math.max(columns.length + 2, 3)} className="muted">
// //                                             {loadingCenters ? "Loading..." : "No centers found (GET /api/centers)"}
// //                                         </td>
// //                                     </tr>
// //                                 ) : (
// //                                     filtered.map((c, idx) => (
// //                                         <tr key={c.centerId || idx}>
// //                                             <td>{idx + 1}</td>
// //                                             {columns.map((k) => (
// //                                                 <td key={k}>
// //                                                     {typeof c[k] === "object" ? JSON.stringify(c[k]) : String(c[k] ?? "")}
// //                                                 </td>
// //                                             ))}
// //                                             <td className="actions">
// //                                                 {/* <button className="btn btn-sm" onClick={() => openEdit(c)}>
// //                                                     Edit
// //                                                 </button> */}
// //                                                 {/* <button className="btn btn-sm" onClick={() => openAuditsByCenter(c.centerId)}>
// //                                                     Audit
// //                                                 </button> */}
// //                                                 <button className="btn btn-sm btn-danger" onClick={() => deactivateCenter(c.centerId)}>
// //                                                     Deactivate
// //                                                 </button>
// //                                             </td>
// //                                         </tr>
// //                                     ))
// //                                 )}
// //                             </tbody>
// //                         </table>
// //                     </div>

// //                     {/* <div className="note">
// //                         <b>Note:</b> Table requires backend <code>GET /api/centers</code>. If not available, tell me and I’ll adjust page to show results only.
// //                     </div> */}
// //                 </div>

// //                 {/* EDIT MODAL (PATCH /api/centers?centerId=...&actor=...) */}


// //                 {editingCenter &&
// //                     createPortal(
// //                         <div className="modal-overlay" onClick={() => setEditingCenter(null)}>
// //                             <div className="ep-modal" onClick={(e) => e.stopPropagation()}>

// //                                 {/* ---------- HEADER ---------- */}
// //                                 <div className="modal-head">
// //                                     <div>
// //                                         <div className="modal-title">Edit Center</div>
// //                                         <div className="modal-sub">
// //                                             Center ID: <b>{editingCenter.centerId}</b>
// //                                         </div>
// //                                     </div>
// //                                     <button className="icon-btn" onClick={() => setEditingCenter(null)}>✕</button>
// //                                 </div>

// //                                 {/* ---------- FORM ---------- */}
// //                                 <div className="modal-body">
// //                                     <div className="form-grid">

// //                                         {/* ---------- PROJECT ---------- */}
// //                                         <FormField
// //                                             label="Project Name"
// //                                             value={editForm.projectName}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, projectName: v }))}
// //                                         />

// //                                         {/* ---------- CENTER BASIC ---------- */}
// //                                         <FormField
// //                                             label="Training Centre Name"
// //                                             value={editForm.trainingCentreName}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, trainingCentreName: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Center Type"
// //                                             placeholder="Training / Assessment"
// //                                             value={editForm.centerType}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, centerType: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="SIDH Center ID"
// //                                             value={editForm.sidhCenterId}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, sidhCenterId: v }))}
// //                                         />

// //                                         {/* ---------- LOCATION ---------- */}
// //                                         <FormField
// //                                             label="Address"
// //                                             value={editForm.address}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, address: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Nearby Landmark"
// //                                             value={editForm.nearbyLandmark}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, nearbyLandmark: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="District"
// //                                             value={editForm.district}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, district: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="State"
// //                                             value={editForm.state}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, state: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Pincode"
// //                                             value={editForm.pincode}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, pincode: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Geo Location"
// //                                             placeholder="lat,long"
// //                                             value={editForm.geoLocation}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, geoLocation: v }))}
// //                                         />

// //                                         {/* ---------- INFRASTRUCTURE ---------- */}
// //                                         <FormField
// //                                             label="Area (Sqft)"
// //                                             type="number"
// //                                             value={editForm.areaSqft}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, areaSqft: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Total Classrooms"
// //                                             type="number"
// //                                             value={editForm.totalClassroom}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, totalClassroom: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Total Labs"
// //                                             type="number"
// //                                             value={editForm.totalLabs}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, totalLabs: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="IT Labs"
// //                                             type="number"
// //                                             value={editForm.itLab}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, itLab: v }))}
// //                                         />

// //                                         {/* ---------- WASHROOM ---------- */}
// //                                         <FormField
// //                                             label="Male Washroom"
// //                                             type="number"
// //                                             value={editForm.maleWashroom}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, maleWashroom: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Female Washroom"
// //                                             type="number"
// //                                             value={editForm.femaleWashroom}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, femaleWashroom: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Unisex Washroom"
// //                                             type="number"
// //                                             value={editForm.unisexWashroom}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, unisexWashroom: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="Reception Area"
// //                                             placeholder="YES / NO"
// //                                             value={editForm.receptionArea}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, receptionArea: v }))}
// //                                         />

// //                                         {/* ---------- SPOC ---------- */}
// //                                         <FormField
// //                                             label="SPOC Name"
// //                                             value={editForm.spocName}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, spocName: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="SPOC Mobile"
// //                                             value={editForm.spocMobile}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, spocMobile: v }))}
// //                                         />

// //                                         <FormField
// //                                             label="SPOC Email"
// //                                             type="email"
// //                                             value={editForm.spocEmail}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, spocEmail: v }))}
// //                                         />

// //                                         {/* ---------- DOCUMENT ---------- */}
// //                                         <FormField
// //                                             label="Address Proof Document"
// //                                             value={editForm.addressProofDocument}
// //                                             onChange={(v) => setEditForm(p => ({ ...p, addressProofDocument: v }))}
// //                                         />

// //                                     </div>


// //                                     {/* ---------- PAYLOAD PREVIEW ---------- */}
// //                                     {/* <div className="preview">
// //                                         <div className="preview-title">PATCH Payload Preview</div>
// //                                         <pre>{JSON.stringify(editForm, null, 2)}</pre>
// //                                     </div> */}
// //                                 </div>

// //                                 {/* ---------- FOOTER ---------- */}
// //                                 <div className="modal-actions">
// //                                     <button className="btn btn-outline" onClick={() => setEditingCenter(null)}>
// //                                         Cancel
// //                                     </button>
// //                                     <button className="btn btn-primary" onClick={patchCenter} disabled={saving}>
// //                                         {saving ? "Saving..." : "Save Changes"}
// //                                     </button>
// //                                 </div>
// //                             </div>
// //                         </div>,
// //                         document.body
// //                     )
// //                 }




// //                 {auditOpen &&
// //                     createPortal(
// //                         <div className="modal-overlay" onClick={closeAudit}>
// //                             <div
// //                                 className="ep-modal ep-modal-wide"
// //                                 onClick={(e) => e.stopPropagation()}
// //                             >
// //                                 {/* ---------- HEADER ---------- */}
// //                                 <div className="modal-head">
// //                                     <div>
// //                                         <div className="modal-title">Center Audit Logs</div>
// //                                         <div className="modal-sub">
// //                                             {auditCenterId ? (
// //                                                 <>Center ID: <b>{auditCenterId}</b></>
// //                                             ) : (
// //                                                 <>All Audits</>
// //                                             )}
// //                                         </div>
// //                                     </div>
// //                                     <button className="icon-btn" onClick={closeAudit}>✕</button>
// //                                 </div>

// //                                 {/* ---------- BODY ---------- */}
// //                                 <div className="modal-body">
// //                                     {auditLoading ? (
// //                                         <div className="muted">Loading audits...</div>
// //                                     ) : auditRows.length === 0 ? (
// //                                         <div className="muted">No audit logs found</div>
// //                                     ) : (
// //                                         <div className="table-wrap">
// //                                             <table>
// //                                                 <thead>
// //                                                     <tr>
// //                                                         <th>ID</th>
// //                                                         <th>Action</th>
// //                                                         <th>Method</th>
// //                                                         <th>Actor</th>
// //                                                         <th>Center</th>
// //                                                         <th>Created At</th>
// //                                                         <th>Before</th>
// //                                                         <th>After</th>
// //                                                     </tr>
// //                                                 </thead>
// //                                                 <tbody>
// //                                                     {auditRows.map((a) => (
// //                                                         <tr key={a.id || `${a.centerId}-${a.createdAt}`}>
// //                                                             <td>{a.id ?? "-"}</td>
// //                                                             <td>{a.action ?? "-"}</td>
// //                                                             <td>{a.method ?? "-"}</td>
// //                                                             <td>{a.actor ?? "-"}</td>
// //                                                             <td>{a.centerId ?? "-"}</td>
// //                                                             <td>{a.createdAt ?? "-"}</td>
// //                                                             <td>
// //                                                                 <pre className="pre-small">
// //                                                                     {String(a.beforeJson ?? "")}
// //                                                                 </pre>
// //                                                             </td>
// //                                                             <td>
// //                                                                 <pre className="pre-small">
// //                                                                     {String(a.afterJson ?? "")}
// //                                                                 </pre>
// //                                                             </td>
// //                                                         </tr>
// //                                                     ))}
// //                                                 </tbody>
// //                                             </table>
// //                                         </div>
// //                                     )}
// //                                 </div>

// //                                 {/* ---------- FOOTER ---------- */}
// //                                 <div className="modal-actions">
// //                                     <button className="btn btn-outline" onClick={closeAudit}>
// //                                         Close
// //                                     </button>
// //                                 </div>
// //                             </div>
// //                         </div>,
// //                         document.body
// //                     )
// //                 }

// //             </div>

// //             <style>{`

// //             .secondaryBtn: {
// //         border: "1px solid #e5e7eb",
// //         background: "#fff",
// //         color: "#111827",
// //         padding: "10px 14px",
// //         borderRadius: 10,
// //         cursor: "pointer",
// //         fontWeight: 900,
// //         whiteSpace: "nowrap",
// //     },
// // .cmp-page {
// //     padding: 22px;
// //     background: #f7f7fb;
// //     min-height: 100vh;
// //     font-family: Inter, system-ui, Arial;
// //     color: #111827;
// // }

// // .cmp-header {
// //     display: flex;
// //     justify-content: space-between;
// //     align-items: flex-end;
// //     gap: 14px;
// //     margin-bottom: 12px;
// // }

// // .cmp-header h1 {
// //     margin: 0;
// //     font-size: 26px;
// //     font-weight: 900;
// //     letter-spacing: 0.2px;
// // }

// // .cmp-header p {
// //     margin: 6px 0 0;
// //     color: #6b7280;
// //     font-size: 13px;
// // }

// // .cmp-header-actions {
// //     display: flex;
// //     gap: 10px;
// //     align-items: center;
// // }

// // .flash {
// //     margin: 12px 0;
// //     padding: 10px 12px;
// //     border-radius: 12px;
// //     font-weight: 800;
// //     font-size: 13px;
// // }

// // .flash-ok {
// //     background: #ecfdf5;
// //     border: 1px solid #a7f3d0;
// //     color: #065f46;
// // }

// // .flash-err {
// //     background: #fef2f2;
// //     border: 1px solid #fecaca;
// //     color: #991b1b;
// // }

// // .card {
// //     background: #fff;
// //     border: 1px solid #e5e7eb;
// //     border-radius: 14px;
// //     padding: 16px;
// //     margin-bottom: 14px;
// //     box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
// // }

// // .card-title {
// //     font-size: 15px;
// //     font-weight: 900;
// //     margin-bottom: 10px;
// // }

// // .card-top {
// //     display: flex;
// //     justify-content: space-between;
// //     gap: 12px;
// //     align-items: center;
// //     margin-bottom: 10px;
// // }

// // .grid-4 {
// //     display: grid;
// //     grid-template-columns: 1.6fr 1fr;
// //     gap: 12px;
// //     align-items: end;
// // }

// // @media (max-width: 980px) {
// //     .grid-4 {
// //         grid-template-columns: 1fr;
// //     }
// // }

// // .field {
// //     display: flex;
// //     flex-direction: column;
// //     gap: 6px;
// // }

// // .field.end {
// //     align-items: flex-end;
// // }

// // .field label {
// //     font-size: 12px;
// //     color: #6b7280;
// //     font-weight: 900;
// // }

// // .field input,
// // .field select {
// //     border: 1px solid #e5e7eb;
// //     border-radius: 12px;
// //     padding: 10px 12px;
// //     background: #fff;
// //     outline: none;
// //     font-size: 14px;
// // }

// // .hint {
// //     font-size: 12px;
// //     color: #6b7280;
// // }

// // .btn {
// //     border: 1px solid #e5e7eb;
// //     background: #fff;
// //     padding: 10px 14px;
// //     border-radius: 12px;
// //     font-weight: 900;
// //     cursor: pointer;
// // }

// // .btn:hover {
// //     filter: brightness(0.98);
// // }

// // .btn-primary {
// //     border-color: #2563eb;
// //     background: #2563eb;
// //     color: #fff;
// // }

// // .btn-outline {
// //     background: #fff;
// // }

// // .btn-sm {
// //     padding: 7px 10px;
// //     border-radius: 10px;
// //     font-size: 12px;
// // }

// // .btn-danger {
// //     border-color: #fca5a5;
// //     background: #fff;
// //     color: #991b1b;
// // }

// // .result {
// //     margin-top: 12px;
// //     border: 1px dashed #e5e7eb;
// //     border-radius: 12px;
// //     padding: 12px;
// //     background: #fafafa;
// // }

// // .result-title {
// //     font-size: 12px;
// //     font-weight: 900;
// //     margin-bottom: 8px;
// // }

// // .result-grid {
// //     display: grid;
// //     grid-template-columns: repeat(5, minmax(130px, 1fr));
// //     gap: 10px;
// // }

// // @media (max-width: 980px) {
// //     .result-grid {
// //         grid-template-columns: 1fr 1fr;
// //     }
// // }

// // .kv {
// //     display: flex;
// //     justify-content: space-between;
// //     gap: 10px;
// //     padding: 8px 10px;
// //     border: 1px solid #eee;
// //     border-radius: 10px;
// //     background: #fff;
// //     font-size: 12px;
// // }

// // .errors {
// //     margin-top: 10px;
// //     border: 1px solid #fecaca;
// //     background: #fff5f5;
// //     border-radius: 12px;
// //     padding: 10px;
// // }

// // .err-item {
// //     font-size: 12px;
// //     font-weight: 800;
// //     color: #991b1b;
// //     padding: 6px 0;
// //     border-bottom: 1px solid rgba(153, 27, 27, 0.15);
// // }

// // .err-item:last-child {
// //     border-bottom: none;
// // }

// // .ok {
// //     margin-top: 10px;
// //     font-size: 12px;
// //     font-weight: 900;
// //     color: #065f46;
// // }

// // .table-actions {
// //     display: flex;
// //     gap: 10px;
// //     align-items: center;
// // }

// // .search {
// //     width: 340px;
// //     max-width: 50vw;
// //     border: 1px solid #e5e7eb;
// //     border-radius: 12px;
// //     padding: 10px 12px;
// //     outline: none;
// //     font-size: 14px;
// // }

// // .table-wrap {
// //     overflow-x: auto;
// // }

// // table {
// //     width: 100%;
// //     border-collapse: collapse;
// // }

// // th {
// //     text-align: left;
// //     font-size: 12px;
// //     color: #6b7280;
// //     font-weight: 900;
// //     padding: 10px 10px;
// //     border-bottom: 1px solid #e5e7eb;
// //     background: #fcfcff;
// //     white-space: nowrap;
// // }

// // td {
// //     padding: 10px 10px;
// //     border-bottom: 1px solid #f1f5f9;
// //     font-size: 13px;
// //     white-space: nowrap;
// // }

// // td.actions {
// //     display: flex;
// //     gap: 8px;
// //     align-items: center;
// // }

// // .muted {
// //     color: #6b7280;
// //     font-weight: 800;
// // }

// // .note {
// //     margin-top: 10px;
// //     font-size: 12px;
// //     color: #6b7280;
// // }

// // /* ---------- modal ---------- */
// // /* ===== MODAL ===== */
// // .modal-overlay {
// //   position: fixed;
// //   inset: 0;
// //   background: rgba(0, 0, 0, 0.45);
// //   display: flex;
// //   align-items: center;
// //   justify-content: center;
// //   z-index: 2147483647;
// // }

// // .ep-modal {
// //   background: #fff;
// //   width: min(900px, 96vw);
// //   max-height: 90vh;
// //   border-radius: 16px;
// //   padding: 18px;
// //   box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
// //   display: flex;
// //   flex-direction: column;
// // }

// // .modal-head {
// //   display: flex;
// //   justify-content: space-between;
// //   align-items: flex-start;
// //   margin-bottom: 12px;
// // }

// // .modal-title {
// //   font-size: 20px;
// //   font-weight: 900;
// // }

// // .modal-sub {
// //   font-size: 12px;
// //   color: #6b7280;
// //   font-weight: 700;
// // }

// // .modal-body {
// //   overflow: auto;
// //   padding-right: 6px;
// // }

// // .form-grid {
// //   display: grid;
// //   grid-template-columns: repeat(2, minmax(0, 1fr));
// //   gap: 14px;
// // }

// // @media (max-width: 768px) {
// //   .form-grid {
// //     grid-template-columns: 1fr;
// //   }
// // }

// // .field {
// //   display: flex;
// //   flex-direction: column;
// //   gap: 6px;
// // }

// // .field label {
// //   font-size: 12px;
// //   font-weight: 800;
// //   color: #6b7280;
// // }

// // .field input {
// //   padding: 10px 12px;
// //   border-radius: 10px;
// //   border: 1px solid #e5e7eb;
// //   font-size: 14px;
// // }

// // .preview {
// //   margin-top: 14px;
// //   background: #fafafa;
// //   border: 1px dashed #e5e7eb;
// //   border-radius: 12px;
// //   padding: 12px;
// // }

// // .preview-title {
// //   font-size: 12px;
// //   font-weight: 900;
// //   margin-bottom: 6px;
// // }

// // .preview pre {
// //   margin: 0;
// //   font-size: 12px;
// //   white-space: pre-wrap;
// //   word-break: break-word;
// // }

// // .modal-actions {
// //   margin-top: 14px;
// //   display: flex;
// //   justify-content: flex-end;
// //   gap: 10px;
// // }

// // .icon-btn {
// //   border: 1px solid #e5e7eb;
// //   background: #fff;
// //   border-radius: 10px;
// //   padding: 6px 10px;
// //   font-weight: 900;
// //   cursor: pointer;
// // }

// //         `}</style>
// //         </>

// //     );


// // }

